import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { AssignmentDTOIsAdministratorDTO, AssignmentDTOIsClientDTO, AssignmentDTOIsCreativeDTO } from 'utils/typeguards'
import { EditingCategory, ProductKind, ProductKindsWithDocumentDraftCheck, ShootingCategory } from 'constants/product'
import { ReactNode, useCallback, useMemo } from 'react'
import { StageOrder, compareAssignmentStageOrder } from 'utils/stages'
import { useGetAssignment, useGetUpsellProducts } from 'dataQueries'

import { AssignmentStage } from 'constants/assignment'
import { StagingType } from 'models/virtualStaging'
import { choosePrimaryProduct } from 'utils/product'
import constate from 'constate'
import { isShootingCategory } from 'utils/validators'
import { useAuth0 } from 'utils/auth'
import { useGalleryConstants } from './GalleryConstants.context'
import { useParams } from 'react-router-dom'
import { useUserData } from 'components/contexts/UserDataContext'

export const AssignmentStageWithDocumentSendToClient = new Set([
  AssignmentStage.DOCUMENTS_SENT_TO_CLIENT,
  AssignmentStage.VISUALS_SENT_TO_CLIENT
])

/** Context provider for gallery assignment */
export const [GalleryAssignmentContextProvider, useGalleryAssignment] = constate(({
  assignmentId,
  children
}: {
  assignmentId: string
  children?: ReactNode
}) => {
  const assignment = useGetAssignment(assignmentId)

  const { roles } = useAuth0()
  const { stagesUnlockingDeliveredAssignment, stagesAfterCTSubmit } = useGalleryConstants()
  const {
    baseUserData,
    organizationData,
    userPropertyWorkspaceId,
    currentUserWorkspace
  } = useUserData()

  const getUpsellProducts = useGetUpsellProducts(assignmentId, userPropertyWorkspaceId!, roles)
  const upsellProduct = useMemo(() => {
    if (!getUpsellProducts.isSuccess || !getUpsellProducts.data?.data) return undefined

    return getUpsellProducts.data.data
  }, [getUpsellProducts.data?.data, getUpsellProducts.isSuccess])

  const assignmentData = useMemo(() => assignment.data?.data, [assignment])
  const assignmentStart = useMemo(() => assignmentData?.shootingStartDateTime, [assignmentData])
  const assignmentRatings = useMemo(() => !!assignmentData && !AssignmentDTOIsCreativeDTO(assignmentData) && assignmentData.ratings || {}, [assignmentData])
  const creative = useMemo(() => (assignmentData && !AssignmentDTOIsCreativeDTO(assignmentData)) ? assignmentData.creative : undefined, [assignmentData])
  const product = useMemo(() => choosePrimaryProduct(assignmentData?.products), [assignmentData])
  const assignmentStage = useMemo(() => assignmentData?.stage, [assignmentData])
  const isProductKindStaging = useMemo(() => !!product && (product.kind === ProductKind.RESIDENTIAL_STAGING || product.kind === ProductKind.COMMERCIAL_STAGING), [product])
  const isProductKindMatterport = useMemo(() => !!product && (product.kind === ProductKind.MATTERPORT_FLOOR_PLAN_WITHOUT_VISIT || product.kind === ProductKind.MATTERPORT_FLOOR_PLAN_WITH_VISIT), [product])
  const isDocumentWithValidateDraft = useMemo(() => assignmentStage === AssignmentStage.DOCUMENTS_RECEIVED_FROM_PROCESSOR && (!!product && ProductKindsWithDocumentDraftCheck.has(product.kind)), [assignmentStage, product])

  const isVisualSendToClient = useMemo(() => assignmentStage === AssignmentStage.VISUALS_SENT_TO_CLIENT, [assignmentStage])
  const isAssignmentCancelled = useMemo(() => !!assignmentData && !AssignmentDTOIsCreativeDTO(assignmentData) && assignmentData.isCancelled, [assignmentData])
  const isDocSendToClient = useMemo(() => assignmentStage && AssignmentStageWithDocumentSendToClient.has(assignmentStage), [assignmentStage])
  const isWaitingPoADoc = useMemo(() => assignmentStage === AssignmentStage.WAITING_FOR_POWER_OF_ATTORNEY_DOCUMENTS, [assignmentStage])
  const isDocumentInStageProgress = useMemo(() =>
    assignmentStage === AssignmentStage.WAITING_FOR_CLIENT_TO_UPLOAD_DOCUMENTS
    || assignmentStage === AssignmentStage.REQUEST_SENT_TO_PROCESSOR
    || assignmentStage === AssignmentStage.DOCUMENTS_RECEIVED_FROM_PROCESSOR, [assignmentStage])

  // Resolve product FeedbackType:
  // 1) product kind is staging and category is real state or staging 
  // or 2) product kind is Matterport floor plan with visit or Matterport floor plan without visit
  // or 3) product kind is Mobile application floor plan
  // or 4) product kind is 2D floor plan editing
  // or 5) product kind is 3D floor plan editing
  const isFeedbackButtonAllowed = useMemo(() => {
    const allowedProductKinds = [
      ProductKind.MOBILE_APPLICATION_FLOOR_PLAN,
      ProductKind.FLOOR_PLAN_EDITING_2D,
      ProductKind.FLOOR_PLAN_EDITING_3D,
    ]

    if ((roles.isClient || roles.isAdmin) && assignmentStage === AssignmentStage.VISUALS_SENT_TO_CLIENT) {
      if (isProductKindStaging && (product?.category === ShootingCategory.REAL_ESTATE
        || product?.category === EditingCategory.STAGING)) {
        return true
      } else if (isProductKindMatterport) {
        return true
      } else if (product && allowedProductKinds.includes(product.kind)) {
        return true
      }
    }

    return false
  }, [isProductKindStaging, product, isProductKindMatterport, assignmentStage, roles.isAdmin, roles.isClient])

  // Select visuals for staging
  const canSelectVisualsForStaging = useMemo(() => (roles.isClient || roles.isAdmin) && !!product && isShootingCategory(product.category) && isProductKindStaging, [roles, product, isProductKindStaging])
  const isAssignmentDelivered = useMemo(() => {
    if (!assignmentStage) return false
    const order = compareAssignmentStageOrder(assignmentStage, stagesUnlockingDeliveredAssignment)
    return (order === StageOrder.COMPARING_EQUAL_TO_COMPARED_TO || order === StageOrder.COMPARING_WITHIN_BOUNDS_OF_COMPARED_TO)
  }, [assignmentStage, stagesUnlockingDeliveredAssignment])
  const selectedFavouriteCreativeId = useMemo(() => !!assignmentData && AssignmentDTOIsAdministratorDTO(assignmentData) ? assignmentData.selectedFavouriteCreative?.creativeId : null, [assignmentData])
  const isRatingsFinished = useMemo(() => {
    // Star rating system: 2 dimensions (VISUALS + CREATIVE), no SERVICE, no text required
    const totalRatingBox = creative ? 2 : 1
    let isAllRated = true
    for (const [, ratingWrap] of Object.entries(assignmentRatings)) {
      if (!ratingWrap?.rating) {
        isAllRated = false
        break
      }
    }
    return !!assignmentData && !AssignmentDTOIsCreativeDTO(assignmentData) && assignmentData.ratings && Object.keys(assignmentData.ratings).length >= totalRatingBox && isAllRated
  }, [assignmentData, assignmentRatings, creative])

  // In some parts of product we require minimal rating all boxes to be finished.
  // With the star rating system there is no text field distinction, so this equals isRatingsFinished.
  const isMinimalRatingsFinished = isRatingsFinished

  // On property route (/property/:id/gallery/:assignmentId/edit) :id is the propertyId,
  // on gallery route (/gallery/:id/edit) :id is the assignmentId — only use it when :assignmentId param exists
  const { id, assignmentId: assignmentIdParam } = useParams<{ id?: string, assignmentId?: string }>()
  const realtyId = assignmentIdParam ? id : undefined


  const logGalleryEvent = useCallback((eventName: AnalyticsEvent, params?: {}, propertyId?: string) => {
    logAnalyticsEvent(eventName, {
      userId: baseUserData?.id,
      userEmail: baseUserData?.email,
      organizationId: organizationData?.id,
      planId: currentUserWorkspace?.id,
      ...(assignmentId && { assignmentId }),
      ...(propertyId && { propertyId }),
      ...(realtyId && { realtyId }),
      ...params
    })
  }, [assignmentId, baseUserData, organizationData?.id, currentUserWorkspace?.id, realtyId])

  const isBKBNDecide = useMemo(() => {
    if (assignmentData && AssignmentDTOIsAdministratorDTO(assignmentData) && assignmentData.stagingDetails) {
      const stagingDetails = assignmentData.stagingDetails
      const hasBKBNtype = Object.values(stagingDetails).some(item => item?.type === StagingType.BKBN)

      return hasBKBNtype
    }
  }, [assignmentData])

  const isAssignmentSubmittedByCT = useMemo(() => assignmentStage && stagesAfterCTSubmit.has(assignmentStage), [assignmentStage, stagesAfterCTSubmit])

  const allowClientToCreateThreads = useMemo(() => {
    if (assignmentData && AssignmentDTOIsClientDTO(assignmentData)) {
      // The assignment stage must not be PRE_PROCESSING_ORDER.
      if (assignmentData.stage === AssignmentStage.PRE_PROCESSING_ORDER) return false

      // For product kind FLOOR_PLAN_CERTIFICATION (as living area calculation) and assignment stage is in DOCUMENTS_RECEIVED_FROM_PROCESSOR.
      if (product?.kind === ProductKind.FLOOR_PLAN_CERTIFICATION && assignmentData.stage === AssignmentStage.DOCUMENTS_RECEIVED_FROM_PROCESSOR) {
        return true
      }

      // Whether the client is allowed to create message threads (prop from BE)
      if (product?.kind !== ProductKind.FLOOR_PLAN_CERTIFICATION && assignmentData.canClientCreateMessageThread) {
        return true
      }
    }

    return false
  }, [assignmentData, product?.kind])

  return {
    assignment,
    assignmentData,
    assignmentStart,
    creative,
    realtyId,
    logGalleryEvent,
    product,
    isRatingsFinished,
    isMinimalRatingsFinished,
    assignmentStage,
    canSelectVisualsForStaging,
    isAssignmentDelivered,
    selectedFavouriteCreativeId,
    isFeedbackButtonAllowed,
    isBKBNDecide,
    isAssignmentSubmittedByCT,
    isDocumentWithValidateDraft,
    allowClientToCreateThreads,
    upsellProduct,
    isVisualSendToClient,
    isAssignmentCancelled,
    isDocSendToClient,
    isWaitingPoADoc,
    isDocumentInStageProgress,
  }
})
